import { createContext } from "react";

type ApplicantReviewDoneFraudTarget = {
  id?: string | number | null;
  AppID?: string | number | null;
};

type ReviewDoneFraudReporter = () => void;

export type ReviewDoneFraudReportingContextValue = {
  registerReviewDoneFraudReporter: (
    applicant: ApplicantReviewDoneFraudTarget | null | undefined,
    reporter: ReviewDoneFraudReporter | null | undefined
  ) => () => void;
  reportFraudForApplicants: () => string[];
  reportFraudForReviewDoneApplicants: () => string[];
} | null;

const normalizeApplicantReviewDoneFraudKey = (
  applicant: ApplicantReviewDoneFraudTarget | null | undefined
): string => {
  const normalizedApplicantId = String(applicant?.id ?? "").trim();
  if (normalizedApplicantId) {
    return `applicant:${normalizedApplicantId}`;
  }

  const normalizedApplicationId = String(applicant?.AppID ?? "").trim();
  if (normalizedApplicationId) {
    return `application:${normalizedApplicationId}`;
  }

  return "";
};

export const registerReviewDoneFraudReporter = ({
  applicant,
  reporter,
  reporters,
}: {
  applicant: ApplicantReviewDoneFraudTarget | null | undefined;
  reporter: ReviewDoneFraudReporter | null | undefined;
  reporters: Map<string, ReviewDoneFraudReporter>;
}): (() => void) => {
  const applicantKey = normalizeApplicantReviewDoneFraudKey(applicant);
  if (!applicantKey || typeof reporter !== "function") {
    return () => {};
  }

  reporters.set(applicantKey, reporter);

  return () => {
    if (reporters.get(applicantKey) === reporter) {
      reporters.delete(applicantKey);
    }
  };
};

export const reportFraudForApplicants = ({
  applicants = [],
  reporters,
}: {
  applicants?: ApplicantReviewDoneFraudTarget[] | null;
  reporters: Map<string, ReviewDoneFraudReporter>;
}): string[] => {
  const invokedApplicantKeys: string[] = [];
  const seenApplicantKeys = new Set<string>();

  (Array.isArray(applicants) ? applicants : []).forEach((applicant) => {
    const applicantKey = normalizeApplicantReviewDoneFraudKey(applicant);
    if (!applicantKey || seenApplicantKeys.has(applicantKey)) {
      return;
    }

    seenApplicantKeys.add(applicantKey);
    const reporter = reporters.get(applicantKey);
    if (!reporter) {
      return;
    }

    reporter();
    invokedApplicantKeys.push(applicantKey);
  });

  return invokedApplicantKeys;
};

export const reportFraudForReviewDoneApplicants = ({
  applicants = [],
  reporters,
}: {
  applicants?: ApplicantReviewDoneFraudTarget[] | null;
  reporters: Map<string, ReviewDoneFraudReporter>;
}): string[] =>
  reportFraudForApplicants({
    applicants,
    reporters,
  });

export const ReviewDoneFraudReportingContext =
  createContext<ReviewDoneFraudReportingContextValue>(null);
